import React, { useState } from 'react';

const PLATFORM_COLORS = {
  meta: '#1877F2',
  facebook: '#1877F2',
  instagram: '#E1306C',
  google: '#34A853',
  linkedin: '#0A66C2',
};

const AdCard = ({ ad, isDarkMode, onToggleExpand }) => {
  const [expanded, setExpanded] = useState(false);
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const cardBg = isDarkMode ? 'bg-gray-900/60' : 'bg-white';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  const toggle = () => {
    setExpanded(!expanded);
    onToggleExpand?.(ad.id, !expanded);
  };

  return (
    <div className={`rounded-lg border ${borderColor} ${cardBg} overflow-hidden`}>
      <button onClick={toggle} className="w-full flex items-center gap-3 p-3 text-left">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white`}
          style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
        >
          Ad
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${textPrimary} truncate`}>{ad.headline}</p>
          <p className={`text-xs ${textMuted} truncate`}>{ad.primaryText?.substring(0, 60)}...</p>
        </div>
        <div className="flex items-center gap-2">
          {ad.status && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ad.status === 'live'
              ? 'bg-green-500/20 text-green-500'
              : ad.status === 'approved'
                ? 'bg-cyan-500/20 text-cyan-500'
                : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
            }`}>
              {ad.status === 'live' ? '🟢 Live' : ad.status === 'approved' ? '✓ Approved' : 'Draft'}
            </span>
          )}
          <svg className={`w-4 h-4 ${textSecondary} transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className={`px-3 pb-3 pt-0 ${isDarkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
          {/* Creative mockup */}
          <div className={`rounded-lg mb-3 overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`} style={{ aspectRatio: '1.91/1' }}>
            {ad.generatedAssets?.image ? (
              <img src={ad.generatedAssets.image} alt={ad.headline} className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center gap-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className={`text-xs ${textSecondary}`}>Ad creative mockup</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted}`}>Primary Text</span>
              <p className={`text-sm ${textPrimary} mt-0.5`}>{ad.primaryText}</p>
            </div>
            <div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted}`}>Description</span>
              <p className={`text-sm ${textPrimary} mt-0.5`}>{ad.description}</p>
            </div>
            <div className="flex items-center gap-3">
              <div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted}`}>CTA</span>
                <p className={`text-sm font-semibold ${textPrimary} mt-0.5`}>{ad.cta}</p>
              </div>
              <div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted}`}>Landing Page</span>
                <p className={`text-sm ${textPrimary} mt-0.5 truncate max-w-[150px]`}>{ad.landingPageUrl}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdSetCard = ({ adSet, isDarkMode, onToggleExpand, onAdToggleExpand }) => {
  const [expanded, setExpanded] = useState(false);
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const cardBg = isDarkMode ? 'bg-gray-900/80' : 'bg-white';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  const toggle = () => setExpanded(!expanded);

  return (
    <div className={`rounded-xl border ${borderColor} ${cardBg} overflow-hidden`}>
      <button onClick={toggle} className="w-full flex items-center gap-3 p-3 text-left">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold text-white`}
          style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
        >
          Set
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${textPrimary} truncate`}>{adSet.adSetName}</p>
          <p className={`text-xs ${textMuted} truncate`}>
            {adSet.targetAudience?.ageRange} • {adSet.targetAudience?.locations?.join(', ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            ₹{adSet.budget?.daily}/day
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {adSet.ads?.length} ad{adSet.ads?.length !== 1 ? 's' : ''}
          </span>
          <svg className={`w-4 h-4 ${textSecondary} transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className={`px-3 pb-3 space-y-2 ${isDarkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
          {/* Targeting summary */}
          <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${borderColor}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted} mb-1.5`}>Targeting</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className={textMuted}>Age:</span> <span className={textPrimary}>{adSet.targetAudience?.ageRange}</span></div>
              <div><span className={textMuted}>Locations:</span> <span className={textPrimary}>{adSet.targetAudience?.locations?.join(', ')}</span></div>
              <div><span className={textMuted}>Interests:</span> <span className={textPrimary}>{adSet.targetAudience?.interests?.join(', ')}</span></div>
              <div><span className={textMuted}>Job Titles:</span> <span className={textPrimary}>{adSet.targetAudience?.jobTitles?.join(', ')}</span></div>
              {adSet.targetAudience?.retargeting && (
                <div className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium bg-purple-500/20 text-purple-400`}>Retargeting Enabled</span>
                </div>
              )}
              {adSet.targetAudience?.lookalike && (
                <div className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium bg-blue-500/20 text-blue-400`}>Lookalike Enabled</span>
                </div>
              )}
            </div>
          </div>

          {/* Budget & Schedule */}
          <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${borderColor}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted} mb-1.5`}>Budget & Schedule</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className={textMuted}>Daily:</span> <span className={textPrimary}>₹{adSet.budget?.daily}</span></div>
              <div><span className={textMuted}>Total:</span> <span className={textPrimary}>₹{adSet.budget?.total}</span></div>
              <div><span className={textMuted}>Start:</span> <span className={textPrimary}>{adSet.schedule?.startDate}</span></div>
              <div><span className={textMuted}>End:</span> <span className={textPrimary}>{adSet.schedule?.endDate}</span></div>
              <div className="col-span-2"><span className={textMuted}>Bidding:</span> <span className={textPrimary}>{adSet.biddingStrategy}</span></div>
            </div>
          </div>

          {/* Ads */}
          <p className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted} px-0.5`}>Ads</p>
          <div className="space-y-2">
            {adSet.ads?.map(ad => (
              <AdCard key={ad.id} ad={ad} isDarkMode={isDarkMode} onToggleExpand={onAdToggleExpand} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const OnlineAdsPreview = ({ campaigns, isDarkMode, onApproveCampaign, onPublishCampaign, onRegenerateCampaign, onFetchPerformance, onBack, submitting }) => {
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  const totalCampaigns = campaigns.length;
  const publishedCampaigns = campaigns.filter(c => c.status === 'live').length;
  const approvedCampaigns = campaigns.filter(c => c.status === 'approved').length;

  const toggleCampaign = (id) => {
    setExpandedCampaigns(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="flex items-start gap-2 mb-4">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30'
        : 'bg-gradient-to-br from-emerald-100 to-green-100 border border-emerald-300'
      }`}>
        <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0 max-w-[90%] md:max-w-[80%]">
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
          🎯 Online Ads Campaigns
        </p>

        <div className={`px-4 py-4 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className={`text-sm font-bold ${textPrimary}`}>Campaign Preview</h4>
              <p className={`text-xs ${textMuted} mt-1`}>
                {totalCampaigns} campaign{totalCampaigns !== 1 ? 's' : ''}. {publishedCampaigns} live, {approvedCampaigns} approved, {totalCampaigns - publishedCampaigns - approvedCampaigns} draft.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${publishedCampaigns > 0
                ? 'bg-green-500/20 text-green-500'
                : isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {publishedCampaigns > 0 ? `${publishedCampaigns} Live` : 'Ready to Publish'}
              </span>
            </div>
          </div>

          {/* Campaigns */}
          <div className="space-y-3 mb-4">
            {campaigns.map(campaign => {
              const platform = (campaign.platform || 'meta').toLowerCase();
              const platformColor = PLATFORM_COLORS[platform] || '#6366f1';
              const isExpanded = expandedCampaigns[campaign.id];
              const totalAdSets = campaign.adSets?.length || 0;
              const totalAds = campaign.adSets?.reduce((sum, as) => sum + (as.ads?.length || 0), 0) || 0;

              return (
                <div key={campaign.id} className={`rounded-xl border ${borderColor} overflow-hidden ${isDarkMode ? 'bg-gray-900/80' : 'bg-white'}`}>
                  <button onClick={() => toggleCampaign(campaign.id)} className="w-full flex items-center gap-3 p-3.5 text-left">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold text-white`}
                      style={{ background: `linear-gradient(135deg, ${platformColor}, ${platformColor}dd)` }}
                    >
                      {platform[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${textPrimary} truncate`}>{campaign.campaignName}</p>
                      <p className={`text-xs ${textMuted} truncate`}>
                        {campaign.platform} • {campaign.objective} • {totalAdSets} ad set{totalAdSets !== 1 ? 's' : ''} • {totalAds} ad{totalAds !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {campaign.status === 'live' && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-500/20 text-green-500`}>🟢 Live</span>
                      )}
                      {campaign.status === 'approved' && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium bg-cyan-500/20 text-cyan-500`}>✓ Approved</span>
                      )}
                      {campaign.roas && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium bg-purple-500/20 text-purple-400`}>ROAS {campaign.roas}x</span>
                      )}
                      <svg className={`w-4 h-4 ${textSecondary} transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className={`px-3 pb-3 space-y-3 ${isDarkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
                      {/* Campaign summary */}
                      <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${borderColor}`}>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider ${textMuted} mb-1.5`}>Campaign Overview</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div><span className={textMuted}>Platform:</span> <span className={textPrimary}>{campaign.platform}</span></div>
                          <div><span className={textMuted}>Objective:</span> <span className={textPrimary}>{campaign.objective}</span></div>
                          <div><span className={textMuted}>Total Budget:</span> <span className={textPrimary}>₹{campaign.totalBudget}</span></div>
                          <div><span className={textMuted}>Duration:</span> <span className={textPrimary}>{campaign.duration} days</span></div>
                        </div>
                      </div>

                      {/* Ad Sets */}
                      <div className="space-y-2">
                        {campaign.adSets?.map(adSet => (
                          <AdSetCard
                            key={adSet.id}
                            adSet={adSet}
                            isDarkMode={isDarkMode}
                            onToggleExpand={(id, expanded) => {/* track if needed */}}
                            onAdToggleExpand={(id, expanded) => {/* track if needed */}}
                          />
                        ))}
                      </div>

                      {/* Campaign actions */}
                      <div className="flex gap-2 pt-1">
                        {!campaign.status && (
                          <button
                            onClick={() => onApproveCampaign(campaign.id)}
                            className="flex-1 py-2 rounded-lg text-xs font-bold text-white transition-all hover:brightness-110"
                            style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                          >
                            Approve Campaign
                          </button>
                        )}
                        {campaign.status === 'approved' && !campaign.status === 'live' && (
                          <button
                            onClick={() => onPublishCampaign(campaign.id)}
                            disabled={submitting}
                            className="flex-1 py-2 rounded-lg text-xs font-bold text-white transition-all hover:brightness-110 disabled:opacity-50"
                            style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                          >
                            {submitting ? 'Publishing...' : 'Publish to ' + campaign.platform}
                          </button>
                        )}
                        {campaign.status === 'live' && (
                          <button
                            onClick={() => onFetchPerformance(campaign.id)}
                            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 ${isDarkMode
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            Refresh Performance
                          </button>
                        )}
                        <button
                          onClick={() => onRegenerateCampaign(campaign.id)}
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${isDarkMode
                            ? 'hover:bg-gray-700 text-gray-400'
                            : 'hover:bg-gray-100 text-gray-500'
                          }`}
                        >
                          Regenerate
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bulk actions */}
          <div className="flex gap-2">
            <button
              onClick={() => campaigns.forEach(c => { if (!c.status) onApproveCampaign(c.id); })}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] hover:shadow-lg hover:shadow-emerald-500/25 hover:brightness-110`}
              style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Approve All Campaigns
            </button>
            <button
              onClick={onBack}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98] ${isDarkMode
                ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 border border-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              Back
            </button>
          </div>

          <div className="flex items-center mt-2 px-1">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineAdsPreview;
